import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Form, Spin } from 'antd'
import { flatten, get, groupBy, map, uniqBy } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  IngredientsAutocheckTree,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  getIncludedAllergensOfId,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  idPropType,
  nodePropType,
  simpleEntityPropType,
} from '../../../common/prop-types'
import { getFormItemLabelAboveConfiguration } from '../../../common/utils'

import { useUpdateAllergenIncludedIngredientsDetailsMutation } from '../../hooks'
import { setAllergenPageIsDirty } from '../../redux/actions'

import { allergenIncludedIngredientsDetailsFormInitialValues } from './initial-values'

const AllergenIncludedIngredientsDetailsForm = ({
  ingredientTreeData,
  allergens,
  includedIngredients,
  includedAllergens,
  allergenId,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.ALLERGEN.SAVE_INGREDIENTS,
  ])

  const [
    updateAllergenIncludedIngredientsDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateAllergenIncludedIngredientsDetailsMutation()

  const initialValues = useMemo(
    () =>
      allergenIncludedIngredientsDetailsFormInitialValues(includedIngredients),
    [includedIngredients],
  )

  const includedIngredientsFromAllergens = useMemo(() => {
    const allergensGroupedById = groupBy(allergens, 'id')

    const includedAllergensFromAllIncludedItems = flatten(
      map(includedAllergens, ({ id }) =>
        getIncludedAllergensOfId(id, allergensGroupedById),
      ),
    )

    return uniqBy(
      flatten(
        map(
          [...includedAllergens, ...includedAllergensFromAllIncludedItems],
          ({ id }) =>
            map(
              get(allergensGroupedById, `${id}[0].includedIngredients`),
              ingredient => ({
                ...ingredient,
                source: `Allergen ${get(
                  allergensGroupedById,
                  `${id}[0].name`,
                )}`,
                sourceBadgeType: 'warning',
              }),
            ),
        ),
      ),
      'id',
    )
  }, [allergens, includedAllergens])

  const handleSaveData = useCallback(
    data => updateAllergenIncludedIngredientsDetails(allergenId, data),
    [allergenId, updateAllergenIncludedIngredientsDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Allergen details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [dispatch, includedIngredients, resetForm])

  useEffect(() => {
    dispatch(setAllergenPageIsDirty({ isIncludedIngredientsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editAllergen"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledVerticalSpace>
        <FormButtons
          permissions={[PERMISSIONS.ALLERGEN.SAVE_INGREDIENTS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
        <Form.Item
          name="allergenIncludedIngredientIds"
          label="Included Ingredients"
          labelCol={getFormItemLabelAboveConfiguration(true)}
        >
          <IngredientsAutocheckTree
            disabled={!isFormEnabled}
            ingredientsFromOtherSources={includedIngredientsFromAllergens}
            treeData={ingredientTreeData}
            includeComponentIngredients
          />
        </Form.Item>
      </StyledVerticalSpace>
    </DirtyPromptForm>
  )
}

AllergenIncludedIngredientsDetailsForm.propTypes = {
  ingredientTreeData: PropTypes.arrayOf(nodePropType).isRequired,
  includedIngredients: PropTypes.arrayOf(PropTypes.any),
  includedAllergens: PropTypes.arrayOf(simpleEntityPropType),
  allergens: PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
  allergenId: idPropType.isRequired,
}

AllergenIncludedIngredientsDetailsForm.defaultProps = {
  includedIngredients: [],
  includedAllergens: [],
}

export default AllergenIncludedIngredientsDetailsForm
